import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import axiosInstance from '../utils/axiosConfig'; // Ensure you're using the configured instance

const TransactionContext = createContext();

export const useTransactions = () => {
  return useContext(TransactionContext);
};

export const TransactionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    page: 1,
    limit: 20
  });
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    category: null,
    sortBy: 'date',
    sortDirection: 'desc'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dailyBudget, setDailyBudget] = useState(null);
  const [spendingSummary, setSpendingSummary] = useState(null);
  const [summaryPeriod, setSummaryPeriod] = useState('month');
  const [hasFetched, setHasFetched] = useState(false);
  
  // Add refs to track in-progress requests
  const isFetchingBudget = useRef(false);
  const isFetchingTransactions = useRef(false);
  const isFetchingSummary = useRef(false);
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  function getToken() {
    return localStorage.getItem('token'); // Adjust this based on where you store the token
  }

  // Fetch transactions with optional filtering and pagination
  const fetchTransactions = useCallback(async (page = 1, newFilters = null) => {
    if (!currentUser || isFetchingTransactions.current) return;
    
    try {
      isFetchingTransactions.current = true;
      setLoading(true);
      
      // Prepare query parameters
      const currentFilters = newFilters || filters;
      const params = {
        page,
        limit: pagination.limit,
        sortBy: currentFilters.sortBy,
        sortDirection: currentFilters.sortDirection
      };
      
      if (currentFilters.startDate) {
        params.startDate = currentFilters.startDate.toISOString();
      }
      
      if (currentFilters.endDate) {
        params.endDate = currentFilters.endDate.toISOString();
      }
      
      if (currentFilters.category) {
        params.category = currentFilters.category;
      }
      
      const token = getToken();
      console.log("Token before API call:", token); // Log the token

      const response = await axiosInstance.get('/transactions', { params });
      
      console.log("Transactions API call successful!");
      setTransactions(response.data.transactions);
      setPagination(response.data.pagination);
    } catch (err) {
      // Check if this is a 404 error (no transactions found for new user)
      if (err.response && err.response.status === 404) {
        console.log("No transactions found - likely a new user");
        // Set empty transactions array instead of showing an error
        setTransactions([]);
        setPagination({
          total: 0,
          pages: 1,
          page: 1,
          limit: pagination.limit
        });
      } else {
        console.error("Error fetching transactions:", err);
        setError(err.message || 'Error fetching transactions');
      }
    } finally {
      setLoading(false);
      isFetchingTransactions.current = false;
    }
  }, [currentUser, filters, pagination.limit]);

  // Fetch daily budget information
  const fetchDailyBudget = useCallback(async () => {
    if (!currentUser || isFetchingBudget.current) return;
    
    console.log("Fetching daily budget...");
    try {
      isFetchingBudget.current = true;
      setLoading(true);
      
      // Use axiosInstance for API calls
      const response = await axiosInstance.get('/transactions/budget/daily');
      
      console.log("Budget API call successful!");
      setDailyBudget(response.data);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // New user, no budget data yet
        console.log("No budget data found - likely a new user");
        setDailyBudget({
          dailyGoal: 0,
          spentToday: 0,
          remainingToday: 0,
          monthlyLimit: 0,
          totalMonthlySpending: 0,
          remainingMonthlyBudget: 0,
          month: new Date().toLocaleString('default', { month: 'long' }),
          year: new Date().getFullYear()
        });
      } else if (err.response && err.response.status === 400) {
        // Monthly spending limit not set yet
        setDailyBudget(null);
      } else {
        console.error("Error fetching daily budget:", err);
        setError(err.message || 'Error fetching daily budget');
      }
    } finally {
      setLoading(false);
      isFetchingBudget.current = false;
    }
  }, [currentUser]);

  // Fetch spending summary by category
  const fetchSpendingSummary = useCallback(async (period = 'month') => {
    if (!currentUser || isFetchingSummary.current) return;
    
    try {
      isFetchingSummary.current = true;
      setLoading(true);
      
      // Use axiosInstance for API calls
      const response = await axiosInstance.get('/transactions/summary/spending', {
        params: { period }
      });
      
      console.log("Summary API call successful!");
      setSpendingSummary(response.data);
      setSummaryPeriod(period);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // New user, no spending summary yet
        console.log("No spending summary data found - likely a new user");
        setSpendingSummary({
          categories: [],
          totalSpent: 0,
          period: period
        });
      } else {
        console.error("Error fetching spending summary:", err);
        setError(err.message || 'Error fetching spending summary');
      }
    } finally {
      setLoading(false);
      isFetchingSummary.current = false;
    }
  }, [currentUser]);

  // Create a new transaction
  const createTransaction = async (transactionData) => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.post(`${API_URL}/transactions`, transactionData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Refresh transactions and budgets after creating a new one
      await fetchTransactions();
      await fetchDailyBudget();
      await fetchSpendingSummary(summaryPeriod);
      
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.message || 'Error creating transaction');
      setLoading(false);
      throw err;
    }
  };

  // Update an existing transaction
  const updateTransaction = async (id, transactionData) => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.put(`${API_URL}/transactions/${id}`, transactionData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Refresh transactions and budgets after updating
      await fetchTransactions();
      await fetchDailyBudget();
      await fetchSpendingSummary(summaryPeriod);
      
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.message || 'Error updating transaction');
      setLoading(false);
      throw err;
    }
  };

  // Delete a transaction
  const deleteTransaction = async (id) => {
    try {
      setLoading(true);
      const token = getToken();
      await axios.delete(`${API_URL}/transactions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Refresh transactions and budgets after deleting
      await fetchTransactions();
      await fetchDailyBudget();
      await fetchSpendingSummary(summaryPeriod);
      
      setLoading(false);
      return true;
    } catch (err) {
      setError(err.message || 'Error deleting transaction');
      setLoading(false);
      throw err;
    }
  };

  // Apply filters to transactions
  const applyFilters = async (newFilters) => {
    setFilters(newFilters);
    return await fetchTransactions(1, newFilters);
  };

  // Change page in pagination 
  const changePage = async (newPage) => {
    return await fetchTransactions(newPage);
  };

  // Load data when currentUser changes
  useEffect(() => {
    const fetchData = async () => {
      if (currentUser && !hasFetched) {
        console.log("Fetching data for the first time...");
        await fetchTransactions();
        await fetchDailyBudget();
        await fetchSpendingSummary();
        setHasFetched(true);
      }
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, hasFetched]);

  const value = {
    transactions,
    pagination,
    filters,
    loading,
    error,
    dailyBudget,
    spendingSummary,
    summaryPeriod,
    hasFetched,
    fetchTransactions,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    fetchDailyBudget,
    fetchSpendingSummary,
    setSummaryPeriod,
    applyFilters,
    changePage,
    setError
  };

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
};

export default TransactionContext;